import { useHistory } from 'react-router';
import pathToRegexp from 'path-to-regexp';
import reduce from 'lodash/reduce';
import toString from 'lodash/toString';
import set from 'lodash/set';
import first from 'lodash/first';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { usePersistFn, useDebounceFn, useThrottleFn } from '@umijs/hooks';

import useGettingPlaylist from './useGettingPlaylist';
import useGettingPlaylists from './useGettingPlaylists';
import useCreatePlaylist from './useCreatePlaylist';
import usePlaylistDelete from './usePlaylistDelete';
import useEditPlaylist from './useEditPlaylist';
import useCopyPlaylist from './useCopyPlaylist';
import useGettingPlaylistContents from './useGettingPlaylistContents';
import useSavePlaylist from './useSavePlaylist';
import { Path } from '../../../routes';
import { useCallback } from 'react';

let deleteTimeoutId = null;
let saveTimeoutId = null;

const usePlaylistCrud = ({ params, onCreateSuccess = () => {} }) => {
  const history = useHistory();
  const {
    playlist,
    onFetchPlaylist,
    loading: gettingLoading,
  } = useGettingPlaylist();
  const { playlists = [], onFetchPlaylists } = useGettingPlaylists();
  const { contents, onFetchContents } = useGettingPlaylistContents();
  const { onSubmit } = useCreatePlaylist({
    onSuccess: ({ id }) => {
      onFetchPlaylists();
      onCreateSuccess();
      history.push(pathToRegexp.compile(Path.PLAYLISTS)({ id }));
    },
  });
  const { onDelete } = usePlaylistDelete({
    onSuccess: () => {
      onFetchPlaylists({
        onSuccess: (playlists) =>
          history.replace(
            pathToRegexp.compile(Path.PLAYLISTS)({
              id: first(playlists).id,
            })
          ),
      });
    },
  });
  const { onCopy } = useCopyPlaylist({
    onSuccess: ({ id }) => {
      onFetchPlaylists();
      history.replace(pathToRegexp.compile(Path.PLAYLISTS)({ id }));
    },
  });
  const { onEditPlaylist } = useEditPlaylist({
    onSuccess: () => {
      onFetchPlaylists();
      onFetchPlaylist(playlist.id);
    },
  });
  const { onSave: handleSave } = useSavePlaylist();
  const onChangeItemDuration = usePersistFn((id, index, value) => {
    const newContents = contents.slice();
    set(newContents, [index, 'duration'], value);
    onSave(params.id)(newContents);
  });

  const calcPlaylistDuration = (items) => {
    const list = items || contents;
    let playlistDuration = reduce(
      list,
      (durationSum, { duration }) =>
        duration ? durationSum + +duration : durationSum,
      0
    );
    let from = toString(Math.trunc(playlistDuration / 60));
    from = from.length < 2 ? `0${from}` : from;
    let to = toString(playlistDuration % 60);
    to = to.length < 2 ? `0${to}` : to;

    return `${from}:${to}`;
  };

  const { run } = useDebounceFn(
    (id, values) => {
      handleSave(id)(values);
    },
    [],
    1000
  );

  const onSave = (id) => (values) => {
    run(id, values);
  };

  return {
    playlist,
    playlists,
    onFetchPlaylist: (id) => {
      onFetchPlaylist(id);
      onFetchContents(id);
    },
    onFetchPlaylists: onFetchPlaylists,
    onSubmit,
    onDelete,
    onEditPlaylist,
    onCopy,
    onSave,
    onChangeItemDuration,
    contents,
    loading: gettingLoading,
    playlistDuration: calcPlaylistDuration(),
    calcPlaylistDuration,
  };
};

export default usePlaylistCrud;
